import { creatorApiClient as api } from './apiClient';
import type {
    LearningProgress,
    UpdateProgressDto,
    CourseProgressSummary,
    CompleteSentenceResult,
    VocabularyWithProgress,
    ReviewVocabularyDto,
    VocabularyStats,
    Note,
    CreateNoteDto,
    UpdateNoteDto,
    NoteFilters,
} from '../types/learning';

const API_BASE = '/learning';

/**
 * Learning Service
 * Handles all API calls for student learning features
 */
export const learningService = {
    // ============================================
    // Progress API
    // ============================================

    /**
     * Get learning progress for a course
     */
    async getProgress(courseId: string, episodeId?: string): Promise<LearningProgress> {
        const params = episodeId ? { episodeId } : {};
        const response = await api.get(`${API_BASE}/courses/${courseId}/progress`, { params });
        return response.data;
    },

    /**
     * Update learning progress
     */
    async updateProgress(courseId: string, updateDto: UpdateProgressDto): Promise<LearningProgress> {
        const response = await api.post(`${API_BASE}/courses/${courseId}/progress`, updateDto);
        return response.data;
    },

    /**
     * Get progress summary for all learning modes (for CourseDetailPage)
     */
    async getProgressSummary(courseId: string): Promise<CourseProgressSummary> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/progress-summary`);
        return response.data;
    },

    /**
     * Save current position for a learning mode (for resume functionality)
     */
    async savePosition(
        courseId: string,
        mode: 'dictation' | 'shadowing' | 'comprehension',
        sentenceIndex: number
    ): Promise<void> {
        await api.post(`${API_BASE}/courses/${courseId}/${mode}/save-position`, { sentenceIndex });
    },

    /**
     * Mark a sentence as completed and update best score
     */
    async completeSentence(
        courseId: string,
        mode: 'dictation' | 'shadowing' | 'comprehension',
        sentenceIndex: number,
        score: number,
        totalSentences: number
    ): Promise<CompleteSentenceResult> {
        const response = await api.post(`${API_BASE}/courses/${courseId}/${mode}/complete-sentence`, {
            sentenceIndex,
            score,
            totalSentences,
        });
        return response.data;
    },

    // ============================================
    // Vocabulary API
    // ============================================

    /**
     * Get vocabulary list for a course
     */
    async getVocabulary(
        courseId: string,
        filters?: { masteryLevel?: number; isFavorite?: boolean }
    ): Promise<VocabularyWithProgress[]> {
        const params: any = {};
        if (filters?.masteryLevel !== undefined) params.masteryLevel = filters.masteryLevel;
        if (filters?.isFavorite !== undefined) params.isFavorite = filters.isFavorite;

        const response = await api.get(`${API_BASE}/courses/${courseId}/vocabulary`, { params });
        return response.data;
    },

    /**
     * Get vocabulary review queue (spaced repetition)
     */
    async getReviewQueue(courseId: string, limit = 20): Promise<VocabularyWithProgress[]> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/vocabulary/review-queue`, {
            params: { limit },
        });
        return response.data;
    },

    /**
     * Get vocabulary statistics
     */
    async getVocabularyStats(courseId: string): Promise<VocabularyStats> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/vocabulary/stats`);
        return response.data;
    },

    /**
     * Review a vocabulary word
     */
    async reviewVocabulary(
        vocabularyId: string,
        reviewDto: ReviewVocabularyDto
    ): Promise<{ newMasteryLevel: number; nextReviewAt: Date; xpEarned: number; leveledUp: boolean; newLevel?: number }> {
        const response = await api.post(`${API_BASE}/vocabulary/${vocabularyId}/review`, reviewDto);
        return response.data;
    },

    /**
     * Toggle favorite status for vocabulary
     */
    async toggleFavorite(vocabularyId: string): Promise<{ isFavorite: boolean }> {
        const response = await api.post(`${API_BASE}/vocabulary/${vocabularyId}/favorite`);
        return response.data;
    },

    // ============================================
    // Notes API
    // ============================================

    /**
     * Get all notes for the current user (across all courses)
     * For the "My Notes" page
     */
    async getAllNotes(filters?: NoteFilters): Promise<Note[]> {
        const params: Record<string, string> = {};
        if (filters?.courseId) params.courseId = filters.courseId;
        if (filters?.isPinned !== undefined) params.isPinned = String(filters.isPinned);
        if (filters?.relatedWord) params.relatedWord = filters.relatedWord;
        if (filters?.tag) params.tag = filters.tag;
        if (filters?.search) params.search = filters.search;

        const response = await api.get(`${API_BASE}/notes`, { params });
        return response.data;
    },

    /**
     * Create a new note
     */
    async createNote(noteDto: CreateNoteDto): Promise<Note> {
        const response = await api.post(`${API_BASE}/notes`, noteDto);
        return response.data;
    },

    /**
     * Get all notes for a course
     */
    async getNotes(courseId: string, filters?: NoteFilters): Promise<Note[]> {
        const params: any = {};
        if (filters?.isPinned !== undefined) params.isPinned = filters.isPinned;
        if (filters?.relatedWord) params.relatedWord = filters.relatedWord;
        if (filters?.tag) params.tag = filters.tag;
        if (filters?.search) params.search = filters.search;

        const response = await api.get(`${API_BASE}/courses/${courseId}/notes`, { params });
        return response.data;
    },

    /**
     * Get a single note
     */
    async getNote(noteId: string): Promise<Note> {
        const response = await api.get(`${API_BASE}/notes/${noteId}`);
        return response.data;
    },

    /**
     * Update a note
     */
    async updateNote(noteId: string, updateDto: UpdateNoteDto): Promise<Note> {
        const response = await api.put(`${API_BASE}/notes/${noteId}`, updateDto);
        return response.data;
    },

    /**
     * Delete a note
     */
    async deleteNote(noteId: string): Promise<void> {
        await api.delete(`${API_BASE}/notes/${noteId}`);
    },

    /**
     * Toggle pin status
     */
    async togglePin(noteId: string): Promise<{ isPinned: boolean }> {
        const response = await api.post(`${API_BASE}/notes/${noteId}/pin`);
        return response.data;
    },

    /**
     * Get notes by timestamp
     */
    async getNotesByTimestamp(
        courseId: string,
        timestamp: number,
        rangeSeconds = 5
    ): Promise<Note[]> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/notes/at/${timestamp}`, {
            params: { rangeSeconds },
        });
        return response.data;
    },

    /**
     * Search notes
     */
    async searchNotes(courseId: string, query: string, limit = 50): Promise<Note[]> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/notes/search`, {
            params: { q: query, limit },
        });
        return response.data;
    },

    /**
     * Get all tags for user's notes in a course
     */
    async getNoteTags(courseId: string): Promise<string[]> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/notes/tags`);
        return response.data;
    },

    /**
     * Export notes
     */
    async exportNotes(courseId: string, format: 'txt' | 'md' = 'md'): Promise<{ content: string; format: string }> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/notes/export`, {
            params: { format },
        });
        return response.data;
    },

    // ============================================
    // Dictation Results API
    // ============================================

    /**
     * Save dictation practice results
     * Updates vocabulary mastery based on dictation performance
     */
    async saveDictationResults(data: {
        courseId: string;
        inputMode?: string;
        results: Array<{
            word: string;
            userInput?: string;
            isCorrect: boolean;
            sentenceIndex: number;
            accuracy?: number;
            attempts?: number;
            timeSpentSeconds?: number;
        }>;
    }): Promise<{
        wordsProcessed: number;
        wordsUpdated: number;
        wordsNotFound: number;
        results: Array<{
            word: string;
            isCorrect: boolean;
            vocabularyId: string | null;
            masteryUpdated: boolean;
            newMasteryLevel: number | null;
        }>;
        guidance?: Array<{
            type: 'phonics' | 'prosody' | 'morphology' | 'sight_word' | 'spelling';
            categoryKey: string;
            word: string;
            expected: string;
            userInput: string;
            tipKey: string;
            tipNamespace: string;
            learnMorePath: string;
            confidence: number;
            tipVars?: Record<string, string>;
        }>;
    }> {
        const response = await api.post(`${API_BASE}/dictation-results`, data);
        return response.data;
    },

    // ============================================
    // Shadowing Results API
    // ============================================

    /**
     * Save shadowing practice results
     * Updates vocabulary mastery based on pronunciation scores
     */
    /**
     * Upload a shadowing recording (WAV blob → server compresses to Opus)
     */
    async uploadShadowingRecording(audioBlob: Blob, metadata: {
        courseId: string;
        sentenceIndex: number;
    }): Promise<{ url: string; size: number; durationMs: number | null }> {
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.wav');
        formData.append('courseId', metadata.courseId);
        formData.append('sentenceIndex', String(metadata.sentenceIndex));
        const response = await api.post(`${API_BASE}/shadowing/upload-recording`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    async saveShadowingResults(data: {
        courseId: string;
        sentenceIndex: number;
        sentenceText: string;
        overallScore: number;
        accuracyScore: number;
        fluencyScore: number;
        completenessScore: number;
        prosodyScore?: number;
        recognizedText?: string;
        mode?: 'echoing' | 'shadowing';
        scriptVisible?: boolean;
        recordingUrl?: string;
        recordingSize?: number;
        durationMs?: number;
        words: Array<{
            word: string;
            accuracyScore: number;
            errorType: string;
            phonemes?: Array<{ phoneme: string; accuracyScore: number }>;
        }>;
    }): Promise<{
        wordsProcessed: number;
        wordsUpdated: number;
        wordsNotFound: number;
        overallScore: number;
        summary: {
            excellent: number;
            acceptable: number;
            needsWork: number;      // score <60 (mastery decay applied)
            omitted: number;
            totalMasteryChange: number;  // can be negative due to decay
        };
        guidance: Array<{
            type: string;
            categoryKey: string;
            word: string;
            expected: string;
            userInput: string;
            tipKey: string;
            tipNamespace: string;
            learnMorePath: string;
            confidence: number;
            tipVars?: Record<string, string>;
        }>;
    }> {
        const response = await api.post(`${API_BASE}/shadowing-results`, data);
        return response.data;
    },

    /**
     * Get shadowing statistics for a course
     */
    async getShadowingStats(courseId: string): Promise<{
        totalPracticeSessions: number;
        averageScore: number;
        wordsImproved: number;
        wordsMastered: number;
    }> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/shadowing-stats`);
        return response.data;
    },

    // ============================================
    // Quiz Results API
    // ============================================

    /**
     * Save quiz results
     * Updates vocabulary mastery based on quiz performance
     */
    async saveQuizResults(data: {
        courseId: string;
        totalQuestions: number;
        correctAnswers: number;
        questionResults: Array<{
            questionId: string;
            questionType: string;
            isCorrect: boolean;
            relatedWords: string[];
        }>;
    }): Promise<{
        questionsProcessed: number;
        wordsUpdated: number;
        wordsNotFound: number;
        score: number;
        summary: {
            correct: number;
            incorrect: number;
            totalMasteryGained: number;
            wordsNeedingReview: string[];
        };
        xpEarned: number;
        leveledUp: boolean;
        newLevel?: number;
    }> {
        const response = await api.post(`${API_BASE}/quiz-results`, data);
        return response.data;
    },

    /**
     * Get quiz statistics for a course
     */
    async getQuizStats(courseId: string): Promise<{
        totalQuizzesTaken: number;
        averageScore: number;
        wordsFromQuizzes: number;
        masteredFromQuizzes: number;
    }> {
        const response = await api.get(`${API_BASE}/courses/${courseId}/quiz-stats`);
        return response.data;
    },

    // ============================================
    // Comprehension Evaluation API
    // ============================================

    /**
     * Evaluate user's translation for comprehension practice
     * Uses Gemini AI to evaluate translation accuracy
     * If score >= 90: vocabulary familiarity increased
     * If score < 90: guidance provided without familiarity change
     */
    async evaluateTranslation(data: {
        courseId: string;
        sentenceIndex: number;
        originalSentence: string;
        correctTranslation: string;
        userTranslation: string;
        locale?: string;
        context?: {
            vocabulary?: Array<{ word: string; definition: string }>;
            grammarPoints?: Array<{ title: string; explanation: string }>;
        };
        surroundingContext?: string;
    }): Promise<{
        isCorrect: boolean;
        overallScore: number;
        errors: Array<{
            type: 'vocabulary' | 'grammar' | 'structure';
            description: string;
            relatedWord?: string;
            relatedGrammar?: string;
            highlightNodeIds?: string[];
        }>;
        feedback: string;
        vocabularyUpdated: boolean;
        wordsUpdatedCount: number;
        xpEarned: number;
        leveledUp: boolean;
        newLevel?: number;
    }> {
        const response = await api.post(`${API_BASE}/comprehension/evaluate-translation`, data);
        return response.data;
    },

    /**
     * Compare 3 Gemini models on the same translation evaluation
     * Test endpoint — no FSRS vocabulary updates
     */
    async compareModels(data: {
        courseId: string;
        sentenceIndex: number;
        originalSentence: string;
        correctTranslation: string;
        userTranslation: string;
        locale?: string;
        context?: {
            vocabulary?: Array<{ word: string; definition: string }>;
            grammarPoints?: Array<{ title: string; explanation: string }>;
        };
        surroundingContext?: string;
    }): Promise<{
        results: Array<{
            model: string;
            modelId: string;
            score: number | null;
            isCorrect: boolean | null;
            errors: Array<{
                type: 'vocabulary' | 'grammar' | 'structure';
                description: string;
                relatedWord?: string;
                relatedGrammar?: string;
            }>;
            feedback: string;
            latencyMs: number;
            error?: string;
        }>;
    }> {
        const response = await api.post(`${API_BASE}/comprehension/compare-models`, data);
        return response.data;
    },

    // ============================================
    // Phonics Error Analysis
    // ============================================

    async submitPhonicsErrors(data: {
        courseId?: string;
        sourceMode: string;
        sentences: Array<{
            expectedText: string;
            totalLetters: number;
            errors: Array<{
                position: number;
                expected: string;
                attempted: string[];
            }>;
            revealedPositions: number[];
            expectedLetters?: string[];
        }>;
    }): Promise<{
        processed: number;
        profileUpdated: boolean;
    }> {
        const response = await api.post(`${API_BASE}/phonics-errors`, data);
        return response.data;
    },

    async getPhonicsProfile(): Promise<{
        confusionMatrix: Record<string, { errors: number; encounters: number; accuracy: number }>;
        topWeaknesses: string[];
        totalLetterAttempts: number;
        totalLetterErrors: number;
        totalHintsUsed: number;
        overallPhonicsAccuracy: number;
    } | null> {
        const response = await api.get(`${API_BASE}/phonics-profile`);
        return response.data;
    },

    async analyzeMorphology(): Promise<MorphologyProfile> {
        const response = await api.post(`${API_BASE}/morphology-analyze`);
        return response.data;
    },

    async getMorphologyProfile(): Promise<MorphologyProfile | null> {
        const response = await api.get(`${API_BASE}/morphology-profile`);
        return response.data;
    },

    async getAllMorphemes(): Promise<any[]> {
        const response = await api.get(`${API_BASE}/morphemes`);
        return response.data;
    },

    async submitSightWordMastery(data: {
        results: Array<{ word: string; correct: boolean }>;
    }): Promise<{ processed: number }> {
        const response = await api.post(`${API_BASE}/sight-words/mastery`, data);
        return response.data;
    },

    async getSightWordMastery(): Promise<Record<string, {
        correctCount: number;
        errorCount: number;
        lastSeenAt: string | null;
    }>> {
        const response = await api.get(`${API_BASE}/sight-words/mastery`);
        return response.data;
    },
};

export interface MorphologyProfile {
    morphemeMap: Record<string, { wordCount: number; masteredCount: number; familiarity: number; userExamples: string[]; category?: string }>;
    categoryMap: Record<string, { totalWords: number; masteredWords: number; familiarity: number }>;
    topWeaknesses: string[];
    topStrengths: string[];
    totalWordsAnalyzed: number;
    wordsWithMorphemes: number;
    overallFamiliarity: number;
}
